import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Scissors, X } from "lucide-react";

const STORAGE_KEY = "he-hairbuy-dismissed";

export default function HairBuyingBadge() {
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const dismissed = sessionStorage.getItem(STORAGE_KEY) === "1";
    if (dismissed) return;
    const t = setTimeout(() => setOpen(true), 1400);
    return () => clearTimeout(t);
  }, []);

  const dismiss = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    sessionStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="hair-buying-badge"
      className="fixed z-[60] bottom-5 left-5 md:bottom-7 md:left-7 max-w-[92vw] pointer-events-none"
    >
      <div
        className={`he-pulse-box pointer-events-auto relative bg-[color:var(--he-ink)] text-[color:var(--he-bg)] border border-[color:var(--he-gold)]/55 transition-[width,padding,border-radius] duration-500 ${
          expanded ? "px-6 py-5 md:px-7 md:py-6 w-[340px] md:w-[400px]" : "pl-3 pr-5 py-3 w-auto"
        }`}
      >
        {/* Dismiss */}
        <button
          aria-label="Bezárás"
          data-testid="hair-buying-badge-close"
          onClick={dismiss}
          className="absolute -top-2.5 -right-2.5 w-6 h-6 rounded-full bg-[color:var(--he-bg)] text-[color:var(--he-ink)] border border-[color:var(--he-gold)]/60 flex items-center justify-center hover:bg-[color:var(--he-gold)] hover:text-[color:var(--he-bg)] transition-colors duration-300"
        >
          <X size={11} strokeWidth={2} />
        </button>

        {/* Compact */}
        {!expanded && (
          <button
            type="button"
            data-testid="hair-buying-badge-toggle"
            onClick={() => setExpanded(true)}
            className="flex items-center gap-3"
            style={{ color: "inherit" }}
          >
            <span className="he-pulse-dot relative inline-flex w-2.5 h-2.5 rounded-full bg-[color:var(--he-gold)] flex-shrink-0" aria-hidden />
            <span
              className="overline-xs whitespace-nowrap"
              style={{ letterSpacing: "0.32em", color: "var(--he-gold)" }}
            >
              Hajfelvásárlás
            </span>
            <span
              className="font-serif-display italic whitespace-nowrap pl-1"
              style={{ fontWeight: 400, fontSize: "0.95rem" }}
            >
              korrekt áron
            </span>
          </button>
        )}

        {/* Expanded */}
        {expanded && (
          <div className="space-y-3.5">
            <div className="flex items-center gap-3">
              <span className="he-pulse-dot relative inline-flex w-2.5 h-2.5 rounded-full bg-[color:var(--he-gold)] flex-shrink-0" aria-hidden />
              <span
                className="overline-xs"
                style={{ letterSpacing: "0.32em", color: "var(--he-gold)" }}
              >
                Aktív hajfelvásárlás
              </span>
            </div>

            <p
              className="font-serif-display leading-[1.12]"
              style={{ fontWeight: 400, fontSize: "1.35rem" }}
            >
              Ne dobd a kukába —{" "}
              <span className="italic text-[color:var(--he-gold)]">korrekt áron felvásároljuk.</span>
            </p>

            <p className="text-[0.85rem] text-[color:var(--he-bg)]/72 leading-relaxed font-light">
              Ha levágatnád a hajadat, vagy nincs már szükséged rá — felvásároljuk.
              Küldj egy fotót és írd meg a hosszát, 24 órán belül árajánlatot adunk.
            </p>

            <div className="flex items-center justify-between pt-1">
              <Link
                to="/kapcsolat"
                data-testid="hair-buying-badge-cta"
                onClick={dismiss}
                className="inline-flex items-center gap-3 group"
                style={{ color: "var(--he-bg)" }}
              >
                <span
                  className="w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-500 group-hover:bg-[color:var(--he-gold)] group-hover:border-[color:var(--he-gold)]"
                  style={{ borderColor: "var(--he-gold)", color: "var(--he-gold)" }}
                >
                  <Scissors size={13} className="group-hover:text-[color:var(--he-ink)] transition-colors duration-500" />
                </span>
                <span className="overline-xs">Ajánlat kérése</span>
              </Link>
              <button
                type="button"
                data-testid="hair-buying-badge-collapse"
                onClick={() => setExpanded(false)}
                className="overline-xs text-[color:var(--he-bg)]/55 hover:text-[color:var(--he-gold)] transition-colors"
              >
                Kicsinyítés
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
